import React, {
    Fragment,
    useEffect,
    useRef,
    useState
} from "react";

import {
    Link,
    useParams
} from "react-router-dom";

import axios from "axios";
import swal from "sweetalert";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const MAX_MESSAGE_LENGTH = 2000;


/* =========================================================
   HELPERS
========================================================= */

const formatDateTime = (value) => {

    if (!value) {
        return "";
    }

    try {

        const normalized =
            String(value).replace(
                " ",
                "T"
            );

        const date =
            new Date(normalized);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }


        return date.toLocaleString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );

    } catch (error) {

        return value;
    }
};


/* =========================================================
   COMPONENT
========================================================= */

const Checkout = () => {

    const { id } =
        useParams();


    /* =====================================================
       STATES
    ===================================================== */

    const [messages, setMessages] =
        useState([]);

    const [messageInput, setMessageInput] =
        useState({

            message: "",

            error_list: {}

        });


    const [loadingMessages, setLoadingMessages] =
        useState(true);

    const [submitting, setSubmitting] =
        useState(false);

    const [pageError, setPageError] =
        useState("");


    /* =====================================================
       REFS
    ===================================================== */

    const chatEndRef =
        useRef(null);

    const textareaRef =
        useRef(null);


    /* =====================================================
       SCROLL TO BOTTOM
    ===================================================== */

    const scrollToBottom =
        (behavior = "smooth") => {

            setTimeout(
                () => {

                    chatEndRef.current?.scrollIntoView({
                        behavior
                    });

                },
                100
            );
        };


    /* =====================================================
       LOAD TICKET
    ===================================================== */

    const loadViewTicketData =
        async (
            showLoader = true
        ) => {

            const authToken =
                localStorage.getItem(
                    "auth_token"
                );


            if (!authToken) {

                setPageError(
                    "Your login session was not found. Please login again."
                );

                setLoadingMessages(false);

                return;
            }


            const data = {

                id: id,

                key_s:
                    API_KEY

            };


            try {

                if (showLoader) {

                    setLoadingMessages(true);
                }


                setPageError("");


                const response =
                    await axios.post(
                        "/view-ticket",
                        data
                    );


                const ticketMessages =
                    Array.isArray(
                        response?.data?.data
                    )
                        ? response.data.data
                        : [];


                setMessages(
                    ticketMessages
                );


                scrollToBottom(
                    showLoader
                        ? "auto"
                        : "smooth"
                );


            } catch (error) {

                console.error(
                    "View Ticket Error:",
                    error
                );


                setPageError(
                    error?.response?.data?.message ||
                    "Unable to load this ticket conversation."
                );


            } finally {

                if (showLoader) {

                    setLoadingMessages(false);
                }
            }
        };


    /* =====================================================
       INITIAL LOAD
    ===================================================== */

    useEffect(() => {

        loadViewTicketData();

        // eslint-disable-next-line
    }, [id]);


    /* =====================================================
       SCROLL WHEN MESSAGES CHANGE
    ===================================================== */

    useEffect(() => {

        if (
            messages.length > 0
        ) {

            scrollToBottom(
                "smooth"
            );
        }

    }, [messages]);


    /* =====================================================
       INPUT
    ===================================================== */

    const handleInput =
        (e) => {

            const {
                name,
                value
            } = e.target;


            if (
                name === "message" &&
                value.length >
                MAX_MESSAGE_LENGTH
            ) {

                return;
            }


            setMessageInput(
                previous => ({

                    ...previous,

                    [name]:
                        value,

                    error_list: {

                        ...previous.error_list,

                        [name]:
                            ""

                    }

                })
            );
        };


    /* =====================================================
       SUBMIT MESSAGE
    ===================================================== */

    const depositSubmit =
        async (e) => {

            e.preventDefault();


            if (submitting) {

                return;
            }


            const message =
                messageInput.message.trim();


            if (!message) {

                setMessageInput(
                    previous => ({

                        ...previous,

                        error_list: {

                            ...previous.error_list,

                            message:
                                "Please enter your message."

                        }

                    })
                );

                return;
            }


            const authToken =
                localStorage.getItem(
                    "auth_token"
                );


            if (!authToken) {

                swal(
                    "Warning",
                    "Please login again.",
                    "warning"
                );

                return;
            }


            const data = {

                message:
                    message,

                ticket_id:
                    id,

                key_s:
                    API_KEY

            };


            try {

                setSubmitting(true);


                const res =
                    await axios.post(
                        "/ticket-submit",
                        data
                    );


                /* =========================================
                   SUCCESS
                ========================================= */

                if (
                    res?.data?.status ===
                    "success"
                ) {

                    setMessageInput({

                        message: "",

                        error_list: {}

                    });


                    await loadViewTicketData(
                        false
                    );


                    textareaRef.current?.focus();


                    swal(
                        "Success",
                        res.data.message ||
                        "Your message has been sent.",
                        "success"
                    );

                }


                /* =========================================
                   FAIL
                ========================================= */

                else if (
                    res?.data?.status ===
                    "fail"
                ) {

                    swal(
                        "Warning",
                        res.data.message ||
                        "Unable to send your message.",
                        "warning"
                    );

                }


                /* =========================================
                   VALIDATION
                ========================================= */

                else {

                    setMessageInput(
                        previous => ({

                            ...previous,

                            error_list:
                                res?.data
                                    ?.validation_error ||
                                {}

                        })
                    );
                }


            } catch (error) {

                console.error(
                    "Ticket Submit Error:",
                    error
                );


                if (
                    error?.response?.status ===
                    422
                ) {

                    setMessageInput(
                        previous => ({

                            ...previous,

                            error_list:
                                error?.response
                                    ?.data
                                    ?.errors ||
                                error?.response
                                    ?.data
                                    ?.validation_error ||
                                {}

                        })
                    );

                    return;
                }


                swal(
                    "Error",
                    error?.response?.data?.message ||
                    "Something went wrong. Please try again.",
                    "error"
                );


            } finally {

                setSubmitting(false);
            }
        };


    /* =====================================================
       ENTER TO SEND
    ===================================================== */

    const handleKeyDown =
        (e) => {

            if (
                e.key === "Enter" &&
                !e.shiftKey
            ) {

                e.preventDefault();


                if (
                    !submitting &&
                    messageInput.message.trim()
                ) {

                    depositSubmit(e);
                }
            }
        };


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                CSS
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .support-chat-page {

                    --primary: #5b4df7;
                    --primary-dark: #4939e8;
                    --primary-soft: #f1efff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --blue: #1570ef;
                    --blue-soft: #eff8ff;

                    --danger: #f04438;
                    --danger-dark: #b42318;
                    --danger-soft: #fef3f2;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .support-chat-container {

                    width: 100%;

                    max-width: 1150px;

                    margin: 0 auto;
                }


                /* =============================================
                   PAGE HEADER
                ============================================= */

                .support-chat-page-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 20px;
                }


                .support-chat-page-title {

                    display: flex;

                    align-items: center;

                    gap: 13px;
                }


                .support-chat-title-icon {

                    width: 50px;
                    height: 50px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 14px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 20px;
                }


                .support-chat-page-title h2 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 25px;

                    line-height: 1.25;

                    font-weight: 800;
                }


                .support-chat-page-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .support-back-button {

                    min-height: 43px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 8px;

                    padding:
                        0 15px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    background:
                        #ffffff;

                    color:
                        var(--text2) !important;

                    font-size: 12px;

                    font-weight: 700;

                    text-decoration:
                        none !important;

                    transition:
                        .15s ease;
                }


                .support-back-button:hover {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary) !important;
                }


                /* =============================================
                   MAIN CHAT CARD
                ============================================= */

                .support-chat-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 18px;

                    background:
                        #ffffff;

                    box-shadow:
                        0 8px 30px
                        rgba(
                            16,
                            24,
                            40,
                            .055
                        );
                }


                /* =============================================
                   CHAT HEADER
                ============================================= */

                .support-chat-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        17px 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;

                    background:
                        #ffffff;
                }


                .support-ticket-info {

                    display: flex;

                    align-items: center;

                    gap: 11px;

                    min-width: 0;
                }


                .support-agent-avatar {

                    position: relative;

                    width: 43px;
                    height: 43px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 12px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 17px;
                }


                .support-online-dot {

                    position: absolute;

                    right: -1px;
                    bottom: -1px;

                    width: 12px;
                    height: 12px;

                    border:
                        2px solid
                        #ffffff;

                    border-radius:
                        50%;

                    background:
                        var(--green);
                }


                .support-ticket-info-text {

                    min-width: 0;
                }


                .support-ticket-info-text h3 {

                    margin:
                        0 0 3px;

                    overflow: hidden;

                    color:
                        var(--text);

                    font-size: 15px;

                    font-weight: 800;

                    text-overflow:
                        ellipsis;

                    white-space: nowrap;
                }


                .support-ticket-meta {

                    display: flex;

                    align-items: center;

                    flex-wrap: wrap;

                    gap: 7px;

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                .support-ticket-number {

                    display: inline-flex;

                    align-items: center;

                    gap: 5px;

                    padding:
                        4px 8px;

                    border-radius: 7px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-weight: 800;
                }


                .support-active-badge {

                    display: inline-flex;

                    align-items: center;

                    gap: 5px;

                    padding:
                        5px 9px;

                    border-radius: 50px;

                    background:
                        var(--green-soft);

                    color:
                        var(--green-dark);

                    font-size: 10px;

                    font-weight: 800;
                }


                .support-active-badge::before {

                    content: "";

                    width: 6px;
                    height: 6px;

                    border-radius: 50%;

                    background:
                        var(--green);
                }


                /* =============================================
                   CHAT AREA
                ============================================= */

                .support-chat-body {

                    position: relative;

                    height: 520px;

                    overflow-x: hidden;
                    overflow-y: auto;

                    padding:
                        25px 22px;

                    background:
                        #f8f9fc;

                    scroll-behavior:
                        smooth;
                }


                .support-chat-body::-webkit-scrollbar {

                    width: 6px;
                }


                .support-chat-body::-webkit-scrollbar-track {

                    background:
                        transparent;
                }


                .support-chat-body::-webkit-scrollbar-thumb {

                    border-radius:
                        20px;

                    background:
                        #d0d5dd;
                }


                /* =============================================
                   CONVERSATION INTRO
                ============================================= */

                .support-chat-intro {

                    display: flex;

                    justify-content:
                        center;

                    margin-bottom: 25px;
                }


                .support-chat-intro span {

                    display: inline-flex;

                    align-items: center;

                    gap: 6px;

                    padding:
                        7px 11px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 50px;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            .9
                        );

                    color:
                        var(--muted);

                    font-size: 10px;

                    font-weight: 600;
                }


                /* =============================================
                   MESSAGE ROW
                ============================================= */

                .support-message-row {

                    display: flex;

                    width: 100%;

                    margin-bottom: 20px;
                }


                .support-message-row.user {

                    justify-content:
                        flex-end;
                }


                .support-message-row.admin {

                    justify-content:
                        flex-start;
                }


                /* =============================================
                   ADMIN
                ============================================= */

                .support-message-admin-wrap {

                    display: flex;

                    align-items:
                        flex-end;

                    gap: 9px;

                    max-width: 76%;
                }


                .support-message-avatar {

                    width: 32px;
                    height: 32px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    margin-bottom: 20px;

                    border-radius: 9px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 12px;
                }


                /* =============================================
                   MESSAGE CONTENT
                ============================================= */

                .support-message-content {

                    min-width: 0;
                }


                .support-message-sender {

                    display: block;

                    margin:
                        0 0 5px 3px;

                    color:
                        var(--muted);

                    font-size: 10px;

                    font-weight: 700;
                }


                .support-message-row.user
                .support-message-sender {

                    margin:
                        0 3px 5px 0;

                    text-align: right;
                }


                .support-message-bubble {

                    position: relative;

                    padding:
                        11px 14px;

                    border-radius:
                        14px;

                    font-size: 13px;

                    line-height: 1.65;

                    white-space:
                        pre-wrap;

                    word-break:
                        break-word;
                }


                .support-message-row.admin
                .support-message-bubble {

                    border:
                        1px solid
                        var(--border);

                    border-bottom-left-radius:
                        4px;

                    background:
                        #ffffff;

                    color:
                        var(--text2);

                    box-shadow:
                        0 2px 8px
                        rgba(
                            16,
                            24,
                            40,
                            .035
                        );
                }


                .support-message-row.user
                .support-message-content {

                    max-width: 72%;
                }


                .support-message-row.user
                .support-message-bubble {

                    border-bottom-right-radius:
                        4px;

                    background:
                        var(--primary);

                    color:
                        #ffffff;

                    box-shadow:
                        0 4px 13px
                        rgba(
                            91,
                            77,
                            247,
                            .16
                        );
                }


                .support-message-time {

                    display: flex;

                    align-items: center;

                    gap: 5px;

                    margin-top: 5px;

                    color:
                        #98a2b3;

                    font-size: 9px;
                }


                .support-message-row.user
                .support-message-time {

                    justify-content:
                        flex-end;
                }


                /* =============================================
                   EMPTY / LOADING
                ============================================= */

                .support-chat-state {

                    height: 100%;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-direction:
                        column;

                    padding: 30px;

                    text-align: center;
                }


                .support-state-icon {

                    width: 56px;
                    height: 56px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin-bottom: 13px;

                    border-radius: 16px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .support-chat-state h4 {

                    margin:
                        0 0 5px;

                    color:
                        var(--text);

                    font-size: 15px;

                    font-weight: 800;
                }


                .support-chat-state p {

                    max-width: 380px;

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.6;
                }


                .support-spinner {

                    width: 38px;
                    height: 38px;

                    margin-bottom: 14px;

                    border:
                        4px solid
                        #e8e9ee;

                    border-top-color:
                        var(--primary);

                    border-radius: 50%;

                    animation:
                        supportSpin
                        .8s linear
                        infinite;
                }


                @keyframes supportSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                .support-retry-button {

                    min-height: 38px;

                    margin-top: 15px;

                    padding:
                        0 15px;

                    border: 0;

                    border-radius: 9px;

                    background:
                        var(--primary);

                    color:
                        #ffffff;

                    font-size: 11px;

                    font-weight: 800;

                    cursor: pointer;
                }


                /* =============================================
                   COMPOSER
                ============================================= */

                .support-composer {

                    padding:
                        17px 20px 18px;

                    border-top:
                        1px solid
                        var(--border);

                    background:
                        #ffffff;
                }


                .support-composer-label {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 8px;
                }


                .support-composer-label strong {

                    color:
                        var(--text2);

                    font-size: 12px;

                    font-weight: 800;
                }


                .support-character-count {

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                .support-character-count.limit {

                    color:
                        var(--danger);
                }


                .support-textarea-wrap {

                    position: relative;
                }


                .support-textarea {

                    display: block;

                    width: 100%;

                    min-height: 105px;
                    max-height: 220px;

                    padding:
                        13px 14px 48px;

                    resize: vertical;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 12px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text);

                    font-family:
                        inherit;

                    font-size: 13px;

                    line-height: 1.6;

                    transition:
                        .18s ease;
                }


                .support-textarea::placeholder {

                    color:
                        #98a2b3;
                }


                .support-textarea:focus {

                    border-color:
                        var(--primary);

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            91,
                            77,
                            247,
                            .08
                        );
                }


                .support-textarea.error {

                    border-color:
                        var(--danger);

                    background:
                        #fffafa;
                }


                .support-textarea-footer {

                    position: absolute;

                    right: 10px;
                    bottom: 9px;
                    left: 11px;

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    pointer-events: none;
                }


                .support-enter-hint {

                    color:
                        #98a2b3;

                    font-size: 9px;
                }


                .support-enter-hint strong {

                    color:
                        var(--muted);
                }


                .support-send-mini-icon {

                    width: 29px;
                    height: 29px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 8px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 11px;
                }


                .support-error-text {

                    display: flex;

                    align-items: center;

                    gap: 5px;

                    margin-top: 6px;

                    color:
                        var(--danger-dark);

                    font-size: 10px;

                    font-weight: 600;
                }


                /* =============================================
                   COMPOSER BOTTOM
                ============================================= */

                .support-composer-bottom {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    margin-top: 11px;
                }


                .support-security-note {

                    display: flex;

                    align-items: center;

                    gap: 6px;

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                .support-send-button {

                    min-width: 135px;
                    height: 43px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 8px;

                    padding:
                        0 17px;

                    border: 0;

                    border-radius: 10px;

                    background:
                        var(--primary);

                    color:
                        #ffffff;

                    font-size: 12px;

                    font-weight: 800;

                    cursor: pointer;

                    transition:
                        .18s ease;

                    box-shadow:
                        0 5px 14px
                        rgba(
                            91,
                            77,
                            247,
                            .17
                        );
                }


                .support-send-button:hover:not(:disabled) {

                    background:
                        var(--primary-dark);

                    transform:
                        translateY(-1px);
                }


                .support-send-button:disabled {

                    opacity: .6;

                    cursor:
                        not-allowed;

                    transform: none;
                }


                .support-button-spinner {

                    width: 15px;
                    height: 15px;

                    border:
                        2px solid
                        rgba(
                            255,
                            255,
                            255,
                            .4
                        );

                    border-top-color:
                        #ffffff;

                    border-radius: 50%;

                    animation:
                        supportSpin
                        .7s linear
                        infinite;
                }


                /* =============================================
                   MOBILE
                ============================================= */

                @media (
                    max-width: 767px
                ) {

                    .support-chat-page {

                        padding: 13px;

                        border-radius: 14px;
                    }


                    .support-chat-page-header {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .support-chat-page-title {

                        align-items:
                            flex-start;
                    }


                    .support-chat-title-icon {

                        width: 44px;
                        height: 44px;

                        border-radius: 12px;
                    }


                    .support-chat-page-title h2 {

                        font-size: 21px;
                    }


                    .support-back-button {

                        width: 100%;
                    }


                    .support-chat-header {

                        padding:
                            14px 15px;
                    }


                    .support-active-badge {

                        display: none;
                    }


                    .support-chat-body {

                        height: 480px;

                        padding:
                            20px 13px;
                    }


                    .support-message-admin-wrap {

                        max-width: 90%;
                    }


                    .support-message-row.user
                    .support-message-content {

                        max-width: 85%;
                    }


                    .support-message-avatar {

                        width: 29px;
                        height: 29px;
                    }


                    .support-message-bubble {

                        padding:
                            10px 12px;

                        font-size: 12px;
                    }


                    .support-composer {

                        padding:
                            14px;
                    }


                    .support-composer-bottom {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .support-send-button {

                        width: 100%;
                    }

                }


                @media (
                    max-width: 420px
                ) {

                    .support-chat-page {

                        padding: 9px;
                    }


                    .support-chat-body {

                        height: 430px;
                    }


                    .support-message-admin-wrap {

                        max-width: 95%;
                    }


                    .support-message-row.user
                    .support-message-content {

                        max-width: 90%;
                    }


                    .support-message-avatar {

                        display: none;
                    }


                    .support-enter-hint {

                        display: none;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="support-chat-page"
            >

                <div
                    className="support-chat-container"
                >


                    {/* =========================================
                        PAGE HEADER
                    ========================================= */}

                    <div
                        className="support-chat-page-header"
                    >

                        <div
                            className="support-chat-page-title"
                        >

                            <div
                                className="support-chat-title-icon"
                            >

                                <i
                                    className="fa fa-comments"
                                    aria-hidden="true"
                                ></i>

                            </div>


                            <div>

                                <h2>
                                    Support Conversation
                                </h2>

                                <p>
                                    View your conversation and
                                    reply to the support team.
                                </p>

                            </div>

                        </div>


                        <Link
                            to="/support-ticket"
                            className="support-back-button"
                        >

                            <i
                                className="fa fa-arrow-left"
                                aria-hidden="true"
                            ></i>

                            All Tickets

                        </Link>

                    </div>


                    {/* =========================================
                        CHAT CARD
                    ========================================= */}

                    <div
                        className="support-chat-card"
                    >


                        {/* =====================================
                            CHAT HEADER
                        ===================================== */}

                        <div
                            className="support-chat-header"
                        >

                            <div
                                className="support-ticket-info"
                            >

                                <div
                                    className="support-agent-avatar"
                                >

                                    <i
                                        className="fa fa-headphones"
                                        aria-hidden="true"
                                    ></i>

                                    <span
                                        className="support-online-dot"
                                    ></span>

                                </div>


                                <div
                                    className="support-ticket-info-text"
                                >

                                    <h3>
                                        Customer Support
                                    </h3>


                                    <div
                                        className="support-ticket-meta"
                                    >

                                        <span
                                            className="support-ticket-number"
                                        >
                                            <i
                                                className="fa fa-ticket"
                                                aria-hidden="true"
                                            ></i>

                                            Ticket #{id}
                                        </span>


                                        <span>
                                            •
                                        </span>


                                        <span>
                                            Support Conversation
                                        </span>

                                    </div>

                                </div>

                            </div>


                            <span
                                className="support-active-badge"
                            >
                                Support
                            </span>

                        </div>


                        {/* =====================================
                            CHAT BODY
                        ===================================== */}

                        <div
                            className="support-chat-body"
                        >


                            {/* LOADING */}

                            {loadingMessages ? (

                                <div
                                    className="support-chat-state"
                                >

                                    <div
                                        className="support-spinner"
                                    ></div>

                                    <h4>
                                        Loading conversation...
                                    </h4>

                                    <p>
                                        Please wait while we
                                        load your ticket messages.
                                    </p>

                                </div>

                            ) : pageError ? (


                                /* =============================
                                   ERROR
                                ============================= */

                                <div
                                    className="support-chat-state"
                                >

                                    <div
                                        className="support-state-icon"
                                    >
                                        <i
                                            className="fa fa-exclamation-triangle"
                                            aria-hidden="true"
                                        ></i>
                                    </div>


                                    <h4>
                                        Unable to load conversation
                                    </h4>


                                    <p>
                                        {pageError}
                                    </p>


                                    <button
                                        type="button"
                                        className="support-retry-button"
                                        onClick={() =>
                                            loadViewTicketData(
                                                true
                                            )
                                        }
                                    >
                                        Try Again
                                    </button>

                                </div>

                            ) : messages.length === 0 ? (


                                /* =============================
                                   EMPTY
                                ============================= */

                                <div
                                    className="support-chat-state"
                                >

                                    <div
                                        className="support-state-icon"
                                    >

                                        <i
                                            className="fa fa-comments"
                                            aria-hidden="true"
                                        ></i>

                                    </div>


                                    <h4>
                                        Start the conversation
                                    </h4>


                                    <p>
                                        There are no messages
                                        in this ticket yet. Send
                                        your first message below.
                                    </p>

                                </div>

                            ) : (

                                <Fragment>


                                    {/* =========================
                                        CONVERSATION LABEL
                                    ========================= */}

                                    <div
                                        className="support-chat-intro"
                                    >

                                        <span>

                                            <i
                                                className="fa fa-lock"
                                                aria-hidden="true"
                                            ></i>

                                            Ticket conversation

                                        </span>

                                    </div>


                                    {/* =========================
                                        MESSAGES
                                    ========================= */}

                                    {messages.map(
                                        (
                                            item,
                                            index
                                        ) => {

                                            const isUser =
                                                item.admin_id ===
                                                    null ||
                                                item.admin_id ===
                                                    undefined;


                                            /* =================
                                               USER MESSAGE
                                            ================= */

                                            if (isUser) {

                                                return (

                                                    <div
                                                        key={
                                                            item.id ||
                                                            index
                                                        }
                                                        className="support-message-row user"
                                                    >

                                                        <div
                                                            className="support-message-content"
                                                        >

                                                            <span
                                                                className="support-message-sender"
                                                            >
                                                                You
                                                            </span>


                                                            <div
                                                                className="support-message-bubble"
                                                            >
                                                                {
                                                                    item.message
                                                                }
                                                            </div>


                                                            <div
                                                                className="support-message-time"
                                                            >

                                                                <i
                                                                    className="fa fa-check"
                                                                    aria-hidden="true"
                                                                ></i>

                                                                {formatDateTime(
                                                                    item.created_at
                                                                )}

                                                            </div>

                                                        </div>

                                                    </div>

                                                );
                                            }


                                            /* =================
                                               ADMIN MESSAGE
                                            ================= */

                                            return (

                                                <div
                                                    key={
                                                        item.id ||
                                                        index
                                                    }
                                                    className="support-message-row admin"
                                                >

                                                    <div
                                                        className="support-message-admin-wrap"
                                                    >

                                                        <div
                                                            className="support-message-avatar"
                                                        >

                                                            <i
                                                                className="fa fa-headphones"
                                                                aria-hidden="true"
                                                            ></i>

                                                        </div>


                                                        <div
                                                            className="support-message-content"
                                                        >

                                                            <span
                                                                className="support-message-sender"
                                                            >
                                                                Support Team
                                                            </span>


                                                            <div
                                                                className="support-message-bubble"
                                                            >
                                                                {
                                                                    item.message
                                                                }
                                                            </div>


                                                            <div
                                                                className="support-message-time"
                                                            >

                                                                <i
                                                                    className="fa fa-clock-o"
                                                                    aria-hidden="true"
                                                                ></i>

                                                                {formatDateTime(
                                                                    item.created_at
                                                                )}

                                                            </div>

                                                        </div>

                                                    </div>

                                                </div>

                                            );
                                        }
                                    )}


                                    {/* AUTO SCROLL TARGET */}

                                    <div
                                        ref={
                                            chatEndRef
                                        }
                                    ></div>

                                </Fragment>
                            )}

                        </div>


                        {/* =====================================
                            MESSAGE FORM
                        ===================================== */}

                        <form
                            onSubmit={
                                depositSubmit
                            }
                            className="support-composer"
                            noValidate
                        >


                            {/* LABEL */}

                            <div
                                className="support-composer-label"
                            >

                                <strong>
                                    Write a Reply
                                </strong>


                                <span
                                    className={
                                        `support-character-count ${
                                            messageInput
                                                .message
                                                .length >
                                            MAX_MESSAGE_LENGTH *
                                                0.9
                                                ? "limit"
                                                : ""
                                        }`
                                    }
                                >

                                    {
                                        messageInput
                                            .message
                                            .length
                                    }

                                    /

                                    {
                                        MAX_MESSAGE_LENGTH
                                    }

                                </span>

                            </div>


                            {/* TEXTAREA */}

                            <div
                                className="support-textarea-wrap"
                            >

                                <textarea
                                    ref={
                                        textareaRef
                                    }
                                    name="message"
                                    value={
                                        messageInput.message
                                    }
                                    onChange={
                                        handleInput
                                    }
                                    onKeyDown={
                                        handleKeyDown
                                    }
                                    className={
                                        `support-textarea ${
                                            messageInput
                                                .error_list
                                                ?.message
                                                ? "error"
                                                : ""
                                        }`
                                    }
                                    rows="4"
                                    placeholder="Type your message here..."
                                    spellCheck="true"
                                    disabled={
                                        submitting
                                    }
                                />


                                <div
                                    className="support-textarea-footer"
                                >

                                    <span
                                        className="support-enter-hint"
                                    >
                                        <strong>
                                            Enter
                                        </strong>
                                        {" "}to send ·{" "}
                                        <strong>
                                            Shift + Enter
                                        </strong>
                                        {" "}for new line
                                    </span>


                                    <span
                                        className="support-send-mini-icon"
                                    >
                                        <i
                                            className="fa fa-paper-plane"
                                            aria-hidden="true"
                                        ></i>
                                    </span>

                                </div>

                            </div>


                            {/* ERROR */}

                            {messageInput
                                .error_list
                                ?.message && (

                                <div
                                    className="support-error-text"
                                >

                                    <i
                                        className="fa fa-exclamation-circle"
                                        aria-hidden="true"
                                    ></i>

                                    <span>

                                        {Array.isArray(
                                            messageInput
                                                .error_list
                                                .message
                                        )
                                            ? messageInput
                                                .error_list
                                                .message[0]
                                            : messageInput
                                                .error_list
                                                .message
                                        }

                                    </span>

                                </div>
                            )}


                            {/* =================================
                                BOTTOM
                            ================================= */}

                            <div
                                className="support-composer-bottom"
                            >

                                <div
                                    className="support-security-note"
                                >

                                    <i
                                        className="fa fa-lock"
                                        aria-hidden="true"
                                    ></i>

                                    Your message will be sent
                                    directly to the support team.

                                </div>


                                <button
                                    type="submit"
                                    className="support-send-button"
                                    disabled={
                                        submitting ||
                                        !messageInput
                                            .message
                                            .trim()
                                    }
                                >

                                    {submitting ? (

                                        <Fragment>

                                            <span
                                                className="support-button-spinner"
                                            ></span>

                                            Sending...

                                        </Fragment>

                                    ) : (

                                        <Fragment>

                                            Send Message

                                            <i
                                                className="fa fa-paper-plane"
                                                aria-hidden="true"
                                            ></i>

                                        </Fragment>
                                    )}

                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default Checkout;